import type {
  PayoutReceiptData,
  PayoutReceiptDestinationType,
  PayoutResponse,
} from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { formatRail } from "@/shared/utils/railDisplay";

export type TransferReceiptOutcome = "settled" | "pending" | "rejected";

export interface TransferReceiptRow {
  label: string;
  value: string;
  mono?: boolean;
  /** Drawn in the brand colour: what the sender did not pay. */
  highlight?: boolean;
  /** The value gets its own line under the label, so a long id stays legible next to a long label. */
  block?: boolean;
}

export interface TransferReceiptSection {
  title: string | null;
  rows: TransferReceiptRow[];
}

export interface TransferReceiptModel {
  outcome: TransferReceiptOutcome;
  title: string;
  date: string;
  status: string;
  /** What a non-settled receipt does not prove. */
  notice: string | null;
  amountLabel: string;
  amount: string;
  currency: string;
  sections: TransferReceiptSection[];
  footer: string;
  /** File name without extension. */
  fileBase: string;
  shareTitle: string;
  shareText: string;
}

export interface TransferReceiptCopy {
  title: string;
  status: Record<TransferReceiptOutcome, string>;
  notice: Record<Exclude<TransferReceiptOutcome, "settled">, string>;
  amountLabel: Record<TransferReceiptOutcome, string>;
  via: string;
  number: string;
  networkReference: string;
  to: string;
  from: string;
  name: string;
  entity: string;
  reference: string;
  hash: string;
  network: string;
  document: string;
  account: string;
  accountValue: (currency: string) => string;
  sentThrough: string;
  senderEntity: string;
  footer: string;
  filePrefix: string;
  destination: Record<PayoutReceiptDestinationType, string>;
  savingsAccount: string;
  checkingAccount: string;
  alias: string;
  shareText: (amount: string, recipient: string | null) => string;
}

export type TransferReceiptPayout = Pick<
  PayoutResponse,
  | "id"
  | "status"
  | "rail"
  | "country"
  | "currency"
  | "net_amount"
  | "created_at"
  | "updated_at"
  | "paid_at"
  | "failed_at"
  | "source_currency"
> & { receipt?: PayoutReceiptData | null };

export interface BuildTransferReceiptInput {
  payout: TransferReceiptPayout;
  /** Signs the receipt while the organization has no identity of its own. */
  organizationName: string | null;
  locale: string;
  copy: TransferReceiptCopy;
}

// A receipt reads in the corridor's own time, wherever it is opened.
const CORRIDOR_TIME_ZONE: Record<string, string> = {
  COL: "America/Bogota",
  BRA: "America/Sao_Paulo",
  MEX: "America/Mexico_City",
  ARG: "America/Argentina/Buenos_Aires",
  CHL: "America/Santiago",
  PER: "America/Lima",
};

const CURRENCY_SYMBOL: Record<string, string> = { BRL: "R$", USDT: "", USDC: "" };

export function formatReceiptDate(iso: string, locale: string, country: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: CORRIDOR_TIME_ZONE[country],
  }).format(new Date(iso));
}

export function receiptMoney(amount: string, currency: string, locale: string): string {
  const symbol = CURRENCY_SYMBOL[currency] ?? "$";
  return `${symbol} ${formatCurrencyAmount(amount, currency, locale)}`.trim();
}

// REVERSED is a failure too: the money went out and came back. Anything unknown is pending, never a verdict.
const REJECTED_STATUSES = new Set(["FAILED", "CANCELLED", "REVERSED"]);

export function payoutReceiptOutcome(status: string): TransferReceiptOutcome {
  const code = status.trim().toUpperCase();
  if (code === "PAID") return "settled";
  return REJECTED_STATUSES.has(code) ? "rejected" : "pending";
}

const lastFour = (value: string) => `••••${value.replace(/\s/g, "").slice(-4)}`;

const MASKED_PIX_KEYS = new Set(["CPF", "CNPJ"]);

/** Whole: the receiver recognises their own key. A Colombian mobile is grouped as people write it. */
export function formatBrebKey(subtype: string | null, value: string): string {
  const digits = value.replace(/\D/g, "");
  return subtype?.toUpperCase() === "PHONE" && /^3\d{9}$/.test(digits)
    ? `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
    : value.trim();
}

export function payoutBrebKey(receipt: PayoutReceiptData | null | undefined): string | null {
  const destination = receipt?.destination;
  return destination?.type === "BREB_KEY" ? formatBrebKey(destination.subtype, destination.value) : null;
}

function destinationRows(
  destination: NonNullable<PayoutReceiptData["destination"]>,
  copy: TransferReceiptCopy,
): TransferReceiptRow[] {
  const subtype = destination.subtype?.toUpperCase() ?? "";
  const label = copy.destination[destination.type];
  switch (destination.type) {
    case "BREB_KEY":
      return [{ label, value: formatBrebKey(destination.subtype, destination.value) }];
    case "PIX_KEY":
      return [
        {
          label,
          value: MASKED_PIX_KEYS.has(subtype) ? lastFour(destination.value) : destination.value.trim(),
        },
      ];
    case "BANK_ACCOUNT":
      return [
        {
          label:
            subtype === "SAVINGS"
              ? copy.savingsAccount
              : subtype === "CHECKING"
                ? copy.checkingAccount
                : label,
          value: lastFour(destination.value),
        },
      ];
    case "CBU_CVU":
      return subtype === "ALIAS"
        ? [{ label: copy.alias, value: destination.value.trim() }]
        : [{ label, value: lastFour(destination.value) }];
    case "CLABE":
      return [{ label, value: lastFour(destination.value) }];
    case "CRYPTO_WALLET":
      return [
        ...(destination.subtype ? [{ label: copy.network, value: destination.subtype }] : []),
        { label, value: destination.value.trim(), mono: true },
      ];
  }
}

/** Written for whoever was paid: fees stay out, and a receipt that is not settled says so in words. */
export function buildTransferReceipt({
  payout,
  organizationName,
  locale,
  copy,
}: BuildTransferReceiptInput): TransferReceiptModel {
  const outcome = payoutReceiptOutcome(payout.status);
  const receipt = payout.receipt ?? null;
  const amount = receiptMoney(payout.net_amount, payout.currency, locale);
  const recipient = receipt?.beneficiary_name?.trim() || null;
  const sender = receipt?.sender ?? null;
  const senderName = sender?.name.trim() || organizationName;
  const at =
    outcome === "settled"
      ? (payout.paid_at ?? payout.updated_at)
      : outcome === "rejected"
        ? (payout.failed_at ?? payout.updated_at)
        : payout.created_at;
  const isUsdt = receipt?.destination?.type === "CRYPTO_WALLET";
  const networkReference = receipt?.network_reference?.trim() || null;

  const sections: TransferReceiptSection[] = [
    {
      title: null,
      rows: [
        { label: copy.via, value: formatRail(payout.rail) },
        { label: copy.number, value: payout.id, mono: true },
        // The one id the receiving bank shows too, so the receiver can tell who paid.
        ...(networkReference
          ? [{ label: copy.networkReference, value: networkReference, mono: true, block: true }]
          : []),
      ],
    },
    {
      title: copy.to,
      rows: [
        ...(recipient ? [{ label: copy.name, value: recipient }] : []),
        ...(receipt?.bank_name ? [{ label: copy.entity, value: receipt.bank_name }] : []),
        ...(receipt?.destination ? destinationRows(receipt.destination, copy) : []),
        ...(receipt?.reference
          ? [{ label: isUsdt ? copy.hash : copy.reference, value: receipt.reference, mono: true }]
          : []),
      ],
    },
    {
      title: copy.from,
      rows: [
        ...(senderName ? [{ label: copy.name, value: senderName }] : []),
        ...(sender?.document_last4
          ? [
              {
                label: copy.document,
                value: [sender.document_type, `••••${sender.document_last4}`].filter(Boolean).join(" "),
              },
            ]
          : []),
        { label: copy.sentThrough, value: copy.senderEntity },
        { label: copy.account, value: copy.accountValue(payout.source_currency || payout.currency) },
      ],
    },
  ].filter((section) => section.rows.length > 0);

  return {
    outcome,
    title: copy.title,
    date: formatReceiptDate(at, locale, payout.country),
    status: copy.status[outcome],
    notice: outcome === "settled" ? null : copy.notice[outcome],
    amountLabel: copy.amountLabel[outcome],
    amount,
    currency: payout.currency,
    sections,
    footer: copy.footer,
    fileBase: `${copy.filePrefix}-${payout.id.slice(0, 8)}`,
    shareTitle: copy.title,
    shareText: copy.shareText(amount, recipient),
  };
}
